import React, { useEffect, useState } from 'react';
import { NewDatePicker } from '../../../../custom-widgets';
import { Box } from '@material-ui/core';
import { MilkClassCheckboxGroup, milkClassDefaults, PoolingStatusRadioGroup } from '../../../../custom-components/form-components/ProcessorMonthlyPoolingInformationSelects';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useFormContext } from 'react-hook-form';
import FMMO from '../../../../constants/pooling';
import { useSelector } from 'react-redux';

function MonthlyPoolingInfo({ props }) {
    const { getValues, setValue, watch } = useFormContext();
    const startDate = useSelector((state) => moment(`${state.persisted.dateDetails.startDate}`, 'YYYY-MM'));
    const [selectedDate, setSelectedDate] = useState(moment());
    const [milkClasses, setMilkClasses] = useState({ ...milkClassDefaults });
    const [isPooled, setIsPooled] = useState(null);
    const [fmmoHistory, setFmmoHistory] = useState(getValues('fmmo_history') ?? []);
    const [milkClassHistory, setMilkClassHistory] = useState(getValues('milk_class_history') ?? []);

    const getRelevantFmmoHistory = () => {
        const fmmoHistoryDesc = fmmoHistory.sort((a, b) => moment(b.date).diff(moment(a.date)));
        const relevantFmmoHistory = fmmoHistoryDesc.find((history) => moment(history.date).isSameOrBefore(selectedDate, 'month'));
        const index = fmmoHistory?.indexOf(relevantFmmoHistory);

        if (_.isEmpty(relevantFmmoHistory)) {
            return { relevantFmmoHistory: null, index };
        }

        return { relevantFmmoHistory, index };
    };

    const getRelevantMilkClassHistory = () => {
        const relevantMilkClassHistory = milkClassHistory.find((history) => moment(history.date, 'YYYY-MM').isSame(selectedDate, 'month'));
        const index = milkClassHistory.indexOf(relevantMilkClassHistory);

        if (_.isEmpty(relevantMilkClassHistory)) {
            return { relevantMilkClassHistory: null, index: -1 };
        }

        return { relevantMilkClassHistory, index };
    };

    const parseFederalOrder = (federalOrder) => {
        const parsedFederalOrder = parseInt(federalOrder);
        if (Number.isNaN(parsedFederalOrder)) {
            return federalOrder;
        }

        return parsedFederalOrder;
    };

    const updatePoolingStatusForMonth = (newPoolingStatus) => {
        const date = selectedDate.startOf('month').toDate();
        const { isPooled: pooled } = newPoolingStatus;
        let createHistory = false;
        // eslint-disable-next-line prefer-const
        let { relevantFmmoHistory, index } = getRelevantFmmoHistory(fmmoHistory, date);

        if (relevantFmmoHistory == null || moment(date).isAfter(moment(relevantFmmoHistory.date), 'month')) {
            createHistory = true;
            relevantFmmoHistory = {
                date,
                fmmos: [],
                is_locked: false,
            };
        }

        if (pooled) {
            setIsPooled(true);
            const federalOrder = parseFederalOrder(getValues('federal_order'));
            relevantFmmoHistory.fmmos = [federalOrder];
        } else {
            setIsPooled(false);
            relevantFmmoHistory.fmmos = [FMMO.Nonpooled];
        }

        if (createHistory) {
            fmmoHistory.push(relevantFmmoHistory);
        } else {
            fmmoHistory[index] = relevantFmmoHistory;
        }

        setFmmoHistory(fmmoHistory);
        setValue('fmmo_history', fmmoHistory);
    };

    const updateSelectedMilkClassesForMonth = (updatedMilkClassStatus) => {
        const updatedMilkClasses = { ...milkClasses, ...updatedMilkClassStatus };
        setMilkClasses(updatedMilkClasses);

        const { relevantMilkClassHistory, index } = getRelevantMilkClassHistory();
        const formattedDate = selectedDate.format('YYYY-MM');

        if (relevantMilkClassHistory == null) {
            const newMilkClassHistory = [...milkClassHistory];
            newMilkClassHistory.push({ date: formattedDate, ...updatedMilkClasses });
            setMilkClassHistory(newMilkClassHistory);
            setValue('milk_class_history', newMilkClassHistory);
            return;
        }

        milkClassHistory[index] = { date: formattedDate, ...updatedMilkClasses };
        setMilkClassHistory(milkClassHistory);
        setValue('milk_class_history', milkClassHistory);
    };

    const isProcessorPooledForMonth = (poolingStatusDecision) => {
        return !_.isEmpty(poolingStatusDecision.fmmos) && !poolingStatusDecision.fmmos.includes(FMMO.Nonpooled);
    };

    useEffect(() => {
        const { relevantFmmoHistory } = getRelevantFmmoHistory();

        if (relevantFmmoHistory == null) {
            setIsPooled(null);
            return;
        }

        const { fmmos } = relevantFmmoHistory;
        setIsPooled(isProcessorPooledForMonth({ fmmos }));
    }, [selectedDate]);

    useEffect(() => {
        const { relevantMilkClassHistory } = getRelevantMilkClassHistory();

        if (relevantMilkClassHistory == null) {
            setMilkClasses({ ...milkClassDefaults });
            return;
        }

        setMilkClasses(relevantMilkClassHistory);
    }, [selectedDate]);

    return (
        <>
            <fieldset
                style={{
                    border: '1px solid rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                    marginTop: '7px',
                    marginBottom: '16px',
                    padding: '8px',
                }}
            >
                <legend
                    style={{
                        marginLeft: '8px',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        color: 'rgba(0,0,0,0.54)',
                        fontSize: '12px',
                    }}
                >
                    Monthly Pooling Information
                </legend>
                <Box display={'flex'} paddingBottom={'32px'} justifyContent={'center'}>
                    <NewDatePicker setSelectedDate={setSelectedDate} selectedDate={selectedDate} startDate={startDate} disableFutureDate />
                </Box>
                {props.fmmoHistoryEnabled && <PoolingStatusRadioGroup isPooled={isPooled} updatePoolingStatusForMonth={(updatedFields) => updatePoolingStatusForMonth(updatedFields)} />}
                {props.milkClassHistoryEnabled && <MilkClassCheckboxGroup milkClasses={milkClasses} updateMilkClassesForMonth={(updatedFields) => updateSelectedMilkClassesForMonth(updatedFields)} />}
            </fieldset>
        </>
    );
}

export default MonthlyPoolingInfo;
